import type { Asset } from "@amzn/hardware-self-service-api-model";
import type {
  AssetStatus,
  DeviceCategory,
  MyHardwareDevice,
  MyHardwareSnapshot,
} from "./my-hardware.types";

/** Typed locally: the pinned model package predates these GetMyStatus fields. */
export interface LcrEligibilityEntry {
  isEligible?: boolean;
  isFutureEolEligible?: boolean;
  nextEligibleDate?: string;
}

export interface MyHardwareWireResponse {
  assets?: Asset[];
  lcrEligibility?: Record<string, LcrEligibilityEntry>;
}

const LAPTOP_CATALOG_CLASS = "computer";

const modelNameFor = (make = "", model = ""): string =>
  make && !model.toLowerCase().startsWith(make.toLowerCase())
    ? [make, model].filter(Boolean).join(" ")
    : model;

const categoryFor = (catalogClass: string): DeviceCategory => {
  const normalized = catalogClass.toLowerCase();
  if (normalized.includes("monitor")) return "monitor";
  if (normalized.includes("laptop") || normalized === LAPTOP_CATALOG_CLASS) return "laptop";
  return "other";
};

const statusFor = (entry?: LcrEligibilityEntry): AssetStatus =>
  entry?.isEligible ? "eligibleForReplacement" : "assigned";

const toDevice = (
  asset: Asset,
  eligibility: Record<string, LcrEligibilityEntry>
): MyHardwareDevice => {
  const entry = asset.id ? eligibility[asset.id] : undefined;
  const device: MyHardwareDevice = {
    assetId: asset.id ?? "",
    serialNumber: asset.serialNumber ?? "",
    make: asset.make ?? "",
    model: asset.model ?? "",
    modelName: modelNameFor(asset.make, asset.model),
    assetTag: asset.assetTag ?? "",
    category: categoryFor(asset.catalogClass ?? ""),
    status: statusFor(entry),
  };

  if (!entry?.isEligible && entry?.isFutureEolEligible && entry.nextEligibleDate) {
    return { ...device, nextEligibleDate: entry.nextEligibleDate };
  }
  return device;
};

export const mapMyHardwareSnapshot = (wire: MyHardwareWireResponse): MyHardwareSnapshot => ({
  devices: (wire.assets ?? []).map((asset) => toDevice(asset, wire.lcrEligibility ?? {})),
});
